import React, { useState, useEffect, useRef } from 'react';
import {
    ArrowLeft,
    MessageSquare,
    Send,
    Loader2,
    Plus
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import AutoLinkText from './AutoLinkText';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const UserInboxPage = ({ onBack }) => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // View state
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);

    const auth = getAuth();
    const user = auth.currentUser;
    const messagesEndRef = useRef(null);

    // Fetch conversations list
    useEffect(() => {
        fetchConversations();
    }, [user]);

    // Fetch messages when a conversation is selected
    useEffect(() => {
        if (selectedConversationId) {
            fetchMessages(selectedConversationId);
        } else {
            setMessages([]);
        }
    }, [selectedConversationId]);

    // Scroll to bottom of messages
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const fetchConversations = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch conversations');
            const data = await response.json();
            setConversations(data);
        } catch (err) {
            console.error(err);
            setError('Failed to load inbox');
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (convId) => {
        if (!user) return;
        setMessagesLoading(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations/${convId}/messages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch messages');
            const data = await response.json();
            setMessages(data);

            // Mark thread as read locally
            // In a real app we might want to refetch the conv list to update badges
            setConversations(prev => prev.map(c =>
                c.id === convId ? { ...c, unread_count: 0 } : c
            ));

        } catch (err) {
            console.error(err);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedConversationId) return;

        setSending(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations/${selectedConversationId}/messages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ content: newMessage })
            });

            if (!response.ok) throw new Error('Failed to send message');

            const savedMessage = await response.json();
            setMessages(prev => [...prev, savedMessage]);
            setNewMessage('');

            // Update last message in list
            setConversations(prev => prev.map(c =>
                c.id === selectedConversationId
                    ? { ...c, last_message: savedMessage.content, updated_at: new Date().toISOString() }
                    : c
            ));

        } catch (err) {
            console.error(err);
            alert("Failed to send reply");
        } finally {
            setSending(false);
        }
    };

    const activeConversation = conversations.find(c => c.id === selectedConversationId);

    // --- RENDER HELPERS ---

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Header */}
            <div className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBack}
                            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                        >
                            <ArrowLeft className="w-6 h-6" />
                        </button>
                        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <MessageSquare className="w-6 h-6 text-blue-600" />
                            Inbox
                        </h1>
                    </div>
                </div>
            </div>

            <main className="flex-1 max-w-4xl mx-auto w-full p-4">

                {/* Two Column Layout (On Desktop) / Stacked (Mobile) */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden min-h-[600px] flex flex-col md:flex-row">

                    {/* LEFT PANEL: CONVERSATION LIST */}
                    <div className={`w-full md:w-1/3 border-r border-gray-100 flex flex-col ${selectedConversationId ? 'hidden md:flex' : 'flex'}`}>
                        <div className="p-4 border-b border-gray-100 bg-gray-50">
                            <h2 className="font-semibold text-gray-700">Conversations</h2>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {conversations.length === 0 ? (
                                <div className="p-8 text-center text-gray-400">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-20" />
                                    <p>No messages yet.</p>
                                </div>
                            ) : (
                                conversations.map(conv => (
                                    <button
                                        key={conv.id}
                                        onClick={() => setSelectedConversationId(conv.id)}
                                        className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedConversationId === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`}
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <div>
                                                <span className="text-xs font-bold text-blue-600 block mb-0.5">From: Admin</span>
                                                <span className="font-semibold text-gray-800 truncate pr-2 block">{conv.subject || "No Subject"}</span>
                                            </div>
                                            {conv.unread_count > 0 && (
                                                <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                                    {conv.unread_count}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 truncate mb-1">
                                            {conv.last_message || "No messages"}
                                        </p>
                                        <span className="text-xs text-gray-400 block text-right">
                                            {new Date(conv.updated_at).toLocaleDateString()}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* RIGHT PANEL: MESSAGE VIEW */}
                    <div className={`w-full md:w-2/3 flex flex-col ${selectedConversationId ? 'flex' : 'hidden md:flex'}`}>
                        {selectedConversationId ? (
                            <>
                                {/* Conversation Header */}
                                <div className="p-4 border-b border-gray-100 flex items-center gap-3 bg-white shadow-sm z-10">
                                    <button
                                        className="md:hidden p-1 -ml-1 hover:bg-gray-100 rounded-full"
                                        onClick={() => setSelectedConversationId(null)}
                                    >
                                        <ArrowLeft className="w-5 h-5 text-gray-500" />
                                    </button>
                                    <div>
                                        <h3 className="font-bold text-gray-800">{activeConversation?.subject || "Conversation"}</h3>
                                        <span className="text-xs text-gray-500">Admin</span>
                                    </div>
                                </div>

                                {/* Messages Area */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                                    {messagesLoading ? (
                                        <div className="flex justify-center p-8">
                                            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                                        </div>
                                    ) : (
                                        messages.map((msg, idx) => {
                                            const isAdmin = msg.sender_type === 'admin';
                                            return (
                                                <div
                                                    key={msg.id}
                                                    className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}
                                                >
                                                    <div
                                                        className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-md ${isAdmin
                                                            ? 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                                                            : 'bg-blue-600 text-white rounded-tr-none'
                                                            }`}
                                                    >
                                                        <p className="whitespace-pre-wrap text-sm leading-relaxed">
                                                            <AutoLinkText
                                                                text={msg.content}
                                                                linkClassName={isAdmin
                                                                    ? "text-blue-600 underline hover:text-blue-800"
                                                                    : "text-blue-100 underline hover:text-white"
                                                                }
                                                            />
                                                        </p>
                                                        <div className={`text-[10px] mt-1 text-right ${isAdmin ? 'text-gray-400' : 'text-blue-100'}`}>
                                                            {formatTime(msg.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Reply Box */}
                                <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-100 flex gap-2">
                                    <input
                                        type="text"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Type your reply..."
                                        className="flex-1 px-4 py-3 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!newMessage.trim() || sending}
                                        className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                    >
                                        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                                    </button>
                                </form>
                            </>
                        ) : (
                            // Empty State (Desktop)
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                                <MessageSquare className="w-16 h-16 mb-4 opacity-20" />
                                <p className="text-lg">Select a conversation to view messages</p>
                            </div>
                        )}
                    </div>
                </div>

            </main>
        </div>
    );
};

export default UserInboxPage;
